// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import capitalize from 'lodash/capitalize';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {currentProjectStore} from 'common/report/stores/project-details';
import {EntityType, SubEntityType} from 'common/report/util/saved-entities/types';
import {userDetailsStore} from 'common/report/stores/user-details';
import {
  getEntityTypeName,
  getEntityTypePermissions,
  isSharableEntityType,
  isSharingEnabledForProject,
} from 'common/report/util/saved-entities';
import {ActionEvent} from 'common/components/banner/types';
import {reporter} from 'common/report/error-reporting';
import {getProjectRoleName} from 'common/widgets/sharing/utils';
import {IronContextRegistry} from 'common/report/context-registry';

import {SharingBannerAttrs} from './types';
import {template} from './template';

const shareBannerKey = `create-entity-share`; // maintain for legacy

export class SharingBanner extends Component<never, SharingBannerAttrs, unknown, unknown, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<SharingBannerAttrs> {
    return {
      ...super.attrsSchema,
      'entity-type': {
        type: `string`,
        description: `Type of entity being created`,
        enum: Object.values(EntityType),
      },
      'sub-entity-type': {
        type: `string`,
        enum: Object.values(SubEntityType),
      },
      'project-id': {type: `number`},
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      helpers: {
        getEntityTypeName,
        getShareBannerText: () => {
          const {t} = this.getContext(`i18n`);
          if (this.attr(`sub-entity-type`) === SubEntityType.BorrowedProperty) {
            return t(`components.create-entity.sharing.everyone`, {context: this.attr(`entity-type`)});
          } else if (getEntityTypePermissions(this.attr(`project-id`), this.attr(`entity-type`)).can_share) {
            return t(`components.create-entity.sharing.share`, {context: this.attr(`entity-type`)});
          }
          return t(`components.create-entity.sharing.private`, {
            context: this.attr(`entity-type`),
            role: capitalize(getProjectRoleName(currentProjectStore.value)),
          });
        },
        handleBannerAction: ({detail}: ActionEvent) => {
          if (detail.actionName === `dismiss`) {
            this.persistShareBannerDismiss();
          }
        },
        shouldShow: () =>
          isSharingEnabledForProject(currentProjectStore.value) &&
          isSharableEntityType(this.attr(`entity-type`)) &&
          !this.getShareBannerDismiss().includes(this.attr(`sub-entity-type`) || this.attr(`entity-type`)),
      },
    };
  }

  persistShareBannerDismiss() {
    const parsedValue = this.getShareBannerDismiss();
    try {
      window.localStorage.setItem(shareBannerKey, JSON.stringify([...parsedValue, this.attr(`entity-type`)]));
      this.update();
    } catch (err) {
      reporter.warn(err, `Unable to persist ${shareBannerKey} to localStorage. User will continue to see the banner.`);
    }
  }

  getShareBannerDismiss(): Array<EntityType | SubEntityType> {
    try {
      const lsValue = window.localStorage.getItem(shareBannerKey);
      return lsValue ? JSON.parse(lsValue) : [];
    } catch (err) {
      reporter.warn(
        err,
        `Unable to retrieve ${shareBannerKey} value from localStorage. User will continue to see the banner.`,
      );
      return [];
    }
  }

  updateCallback = () => {
    this.update();
  };

  connectedCallback(): void {
    super.connectedCallback();
    userDetailsStore.subscribe(this.updateCallback).fetch();
    currentProjectStore.subscribe(this.updateCallback).fetch();
  }
  disconnectedCallback(): void {
    userDetailsStore.unsubscribe(this.updateCallback);
    currentProjectStore.unsubscribe(this.updateCallback);
    super.disconnectedCallback();
  }
}

export const SharingBannerEl = defineAndMakeJsxFactory(`mp-sharing-banner`, SharingBanner);
export const SharingBannerReact = panel2react(`mp-sharing-banner`, SharingBanner);
